import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, TrendingDown, Layers } from "lucide-react";
import { getCollateral } from "../../lib/collateral";
import { PRODUCTS, COMPANY, IMAGES } from "../../lib/data";
import { CollateralBar } from "../../components/site/CollateralKit";

export default function ProductROIBrief() {
  const { product } = useParams();
  const c = getCollateral(product);
  const p = PRODUCTS.find((x) => x.slug === product);
  if (!c || !p) return <Navigate to="/solutions" replace />;
  const cb = p.costBenefit;
  const pdf = `/${c.slug}-roi-brief.pdf`;

  return (
    <div className="min-h-screen bg-charcoal/20 print:bg-white">
      <CollateralBar product={c.slug} label="ROI / Cost-Benefit · One-Page" pdf={pdf} testid="roi" />
      <div className="py-8 print:py-0 flex justify-center">
        <div data-testid="collateral-sheet" className="bg-white shadow-2xl print:shadow-none w-[297mm] h-[210mm] p-[11mm] flex flex-col" style={{ fontSize: "10px" }}>
          <header className="flex items-start justify-between border-b-2 border-ink pb-3">
            <div className="max-w-[64%]">
              <div className="flex items-center gap-2 mb-1.5">
                <img src={IMAGES.markDark} alt="OneHarvest" className="h-6 w-6 object-contain" />
                <span className="font-display font-extrabold tracking-tight text-ink text-sm">ONEHARVEST</span>
                <span className="ml-2 border border-flame/40 bg-flame/10 text-flame px-2 py-0.5 text-[8px] uppercase tracking-[0.18em]">{c.name} · Cost-Benefit Brief</span>
              </div>
              <h1 className="font-display font-black tracking-tight text-ink leading-[1] text-[31px]">{c.roiHeadline}</h1>
              <p className="mt-2 text-charcoal leading-snug text-[11px]">{c.roiSub}</p>
            </div>
            <div className="w-[33%] bg-ink text-white p-3 grid grid-cols-2 gap-2 text-center">
              {c.roiChips.map(([v, l]) => (
                <div key={l}><div className="font-display font-extrabold text-flame text-[22px] leading-none">{v}</div><div className="text-[7.5px] uppercase tracking-wider text-white/50 mt-1">{l}</div></div>
              ))}
            </div>
          </header>

          <div className="flex-1 grid grid-cols-5 gap-5 pt-4 min-h-0">
            <div className="col-span-3 border border-border bg-white p-5 flex flex-col">
              <div className="flex items-center gap-2 mb-1"><Layers className="h-4 w-4 text-flame" /><h2 className="font-display font-bold text-ink text-[13px]">{cb.savingsHeadline}</h2></div>
              <p className="text-muted-foreground text-[10px] mb-3">{cb.intro}</p>
              <div className="flex-1 flex flex-col justify-center space-y-3">
                {cb.metrics.map((m) => (
                  <div key={m.label}>
                    <div className="flex justify-between text-[10px] mb-1"><span className="text-charcoal">{m.label}</span><span className="font-mono text-ink"><span className="text-muted-foreground">{cb.conventionalLabel}: {m.conventional}</span> → <span className="text-flame font-semibold">{c.name}: {m.oneharvest}</span></span></div>
                    <div className="h-3 bg-muted overflow-hidden relative">
                      <div className="absolute inset-y-0 left-0 bg-[#8A93A0]" style={{ width: `${m.conventional}%` }} />
                      <div className="absolute inset-y-0 left-0 bg-flame" style={{ width: `${m.oneharvest}%` }} />
                    </div>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-5 mt-3 text-[9px]"><span className="inline-flex items-center gap-1.5"><span className="h-1 w-4 bg-[#8A93A0] inline-block" /> {cb.conventionalLabel} (baseline 100)</span><span className="inline-flex items-center gap-1.5"><span className="h-1 w-4 bg-flame inline-block" /> {c.name} (indexed)</span></div>
            </div>

            <div className="col-span-2 flex flex-col gap-4">
              <img src={c.image} alt={c.name} className="w-full h-32 object-contain bg-white border border-border p-2" />
              <div className="bg-ink text-white p-4 flex-1 flex flex-col justify-center">
                <div className="text-[8px] uppercase tracking-[0.18em] text-white/50">The takeaway</div>
                <div className="mt-2 flex items-center gap-2"><TrendingDown className="h-6 w-6 text-flame" /><div className="font-display font-extrabold text-[20px] leading-tight">{cb.savingsHeadline}</div></div>
                <p className="mt-3 text-white/55 text-[9.5px] leading-relaxed">{cb.disclaimer}</p>
              </div>
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between gap-4 border-t border-border pt-3">
            <div><div className="font-display font-extrabold text-ink text-[15px]">Model these numbers against your operation.</div><div className="text-muted-foreground text-[10px] mt-0.5">{COMPANY.phone} · {COMPANY.email}</div></div>
            <Link to={`/collateral/${c.slug}/calculator`} className="inline-flex items-center gap-1.5 bg-flame text-white px-5 py-3 font-semibold text-[12px] hover:bg-flame/90 transition-colors print:hidden flex-shrink-0">Open the ROI Calculator <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <p className="mt-2 text-[8px] text-muted-foreground text-center">Figures indexed to a conventional baseline of 100 and are illustrative estimates. Not a quote, guarantee, or warranty of any specific result; provided for evaluation only and not financial, engineering, or legal advice. Actual outcomes vary by site conditions, usage &amp; market pricing. © {new Date().getFullYear()} {COMPANY.legal}.</p>
        </div>
      </div>
    </div>
  );
}
